import { SharedDestinationEntryVM, SharedDestinationVM } from 'src/components/dispatch/detailPanel/sharedDestinationVM';
import { isAssignedDelivery } from 'src/components/dispatch/vehicleRowExpand/assignedDelivery';
import { ExpandedRowVM } from 'src/components/dispatch/vehicleRowExpand/buildExpandedRowVM';
import { VehicleRowGroup } from 'src/pages/ConvenienceDispatches/adapter';

/**
 * 同じ納品先（placeKey）を訪れている車両の一覧を組む。distinct 車両が 2 台未満なら null
 * （＝通常の詳細パネルを開く）。
 *
 * ## 突合は placeKey（＝設定に従った納品先の単位）
 * このパネルは「同じ納品先を複数台で分納している」ことを出すもので、バッジも「N台で分納」。
 * 「配送リストの地点表示 = 名称でまとめる」設定では同座標でも名称が違えば別の納品先なので、
 * 座標で束ねてはいけない（見出しの店名・住所は先頭カードのもの・数量は別店舗ぶんまで
 * 合算されるため、嘘の表示になる）。
 *
 * ## 走査は stop 起点
 * 行は「訪問」ごとに分ける。カードから引くと、同じ地点を 2 回訪問する車両で 1 行に潰れる
 * （groupOpsToCards が非連続の同地点を 1 カードにマージするため。buildOperationPlaces.ts の
 * コメント参照）。stop から引き、その stop が指すカード（row.cards[stop.cardIndex]）が
 * 対象地点かどうかで絞る。
 *
 * ## 数量は stop.metrics
 * カードの値は地点カード全体の合算なので、再訪する車両で両方の行に同じ値が載り totals が
 * 二重計上になる。訪問単位の metrics（Task 14）をそのまま詰め替える。
 *
 * ## seriesIndex と color は引数の値をそのまま使う
 * 地図マーカーが使っているのと同じ VM（expandedRowVMByRowId）と route の色を受け取り、
 * ここでは組み直さない。組み直すと画面ごとに番号がずれる。
 */
export function buildSharedDestinationVM(
  placeKey: string,
  rowGroups: readonly VehicleRowGroup[],
  expandedRowVMByRowId: ReadonlyMap<string, ExpandedRowVM>,
  colorByRowId: ReadonlyMap<string, string>,
): SharedDestinationVM | null {
  const entries: SharedDestinationEntryVM[] = [];
  const vehicleIds = new Set<string>();
  let storeName = '';
  let address: string | undefined;

  for (const group of rowGroups) {
    for (const row of group.rows) {
      const color = colorByRowId.get(row.rowId) ?? '';
      for (const stop of expandedRowVMByRowId.get(row.rowId)?.stops ?? []) {
        const card = row.cards[stop.cardIndex];
        // Why not isAssignedDelivery だけ: 述語が boolean なので TS が DeliveryCard に絞れず
        // storeName / storeAddress を読めない。正本を型述語に変えると共有側の全呼び出しに
        // 影響するため、ここで type を明示して narrowing する（判定内容は同じ）。
        if (!card || card.type !== 'delivery' || !isAssignedDelivery(card)) continue;
        if (card.placeKey !== placeKey) continue;

        if (entries.length === 0) {
          storeName = card.storeName;
          address = card.storeAddress;
        }
        vehicleIds.add(group.vehicle.id);
        entries.push({
          rowId: row.rowId,
          cardIndex: stop.cardIndex,
          dispatchOrderIndex: stop.dispatchOrderIndex,
          vehicleNumber: group.vehicle.vehicleNumber,
          driverName: group.vehicle.driverName,
          companyName: group.vehicle.companyName,
          rotationNumber: row.rotationNumber,
          seriesIndex: stop.seriesIndex,
          color,
          arrivalTime: stop.time,
          metrics: {
            cases: stop.metrics.itemCount,
            weight: stop.metrics.weightKg,
            volume: stop.metrics.volumeM3,
            invoiceCount: stop.metrics.invoiceCount,
          },
        });
      }
    }
  }

  // 1 台だけなら分納ではない。同一車両が複数回転で訪れているケースもここで落ちる
  if (vehicleIds.size < 2) return null;

  const totals = entries.reduce(
    (acc, entry) => ({
      cases: acc.cases + entry.metrics.cases,
      weight: acc.weight + entry.metrics.weight,
      volume: acc.volume + entry.metrics.volume,
      invoiceCount: acc.invoiceCount + entry.metrics.invoiceCount,
    }),
    { cases: 0, weight: 0, volume: 0, invoiceCount: 0 },
  );

  return { storeName, address, vehicleCount: vehicleIds.size, entries, totals };
}
